// @flow

import React, {useState} from 'react';
import {
  CircularProgress,
  makeStyles,
  MenuItem,
  Tooltip,
} from '@material-ui/core';
import {InfoOutlined} from '@material-ui/icons';
import {colors} from '../../../../../../../../assets/styles/colors';

import type {Theme} from '@material-ui/core';
import CustomButton from '../../../../../../../../elements/CustomButton/CustomButton';
import CustomSelect from '../../../../../../../../elements/CustomSelect/CustomSelect';
import {
  useCurrentConversationState,
  useCurrentConversationActions,
  useCurrentConversation,
  useConversationsSynchronizing,
  useConversationIdSelected,
  useConversations,
} from '../../../../../utils/conversation';
import {useCollaborators} from '../../../../../../../configure/accounts/utils';
import Toast from '../../../../../../../../elements/Toast';
import TiendadaTooltip from '../../../../../../../../elements/tiendada-tooltip';
type Props = {};

const useStyles = makeStyles((theme: Theme) => ({
  actions: {
    backgroundColor: colors.grey200,
    padding: theme.spacing(1.5),
  },
  content: {
    display: 'flex',
    justifyContent: 'space-between',
  },
}));

const EMPTY_COLLABORATOR = 'empty';
const ADMIN_OPTION = 'admin';

export default function StickyActions(props: Props) {
  const classes = useStyles();

  const {resetConversationIdSelected} = useConversationIdSelected();
  const {isSynchronizingConversations} = useConversationsSynchronizing();
  const {collaborators} = useCollaborators();
  const {
    state,
    toggleConversationState,
    loading,
    error,
  } = useCurrentConversationState();

  const [changeStateSuccess, setChangeStateSuccess] = useState(false);

  const {
    assignCollaborator,
    assignToMe,
    clearAssignment,
  } = useCurrentConversationActions();

  const {currentConversation: conversation} = useCurrentConversation();
  const conversations = useConversations();

  const stateLabel = state === 'OPEN' ? 'Cerrar' : 'Abrir';

  const getAssigned = () => {
    if (!conversation) return EMPTY_COLLABORATOR;

    const {userId, userType} = conversation;
    const admin = userType === 'VENDOR';
    const collaboratorId =
      userType === 'COLLABORATOR' && userId && `C${userId}`;
    if (admin) return ADMIN_OPTION;
    if (collaboratorId) return collaboratorId;
    return EMPTY_COLLABORATOR;
  };

  const assigned = getAssigned();

  const handleToggleChatState = () => {
    toggleConversationState().then(() => {
      setChangeStateSuccess(true);
    });
    resetConversationIdSelected();
  };

  return (
    <div className={classes.actions}>
      <div className={classes.content}>
        <Tooltip
          title="Las conversaciones cerradas no se pueden asignar"
          disableHoverListener={state !== 'CLOSED'}
        >
          <div>
            <CustomSelect
              pagination
              value={assigned}
              disabled={state === 'CLOSED'}
              onChange={e => {
                const value = e.target.value;

                if (value === EMPTY_COLLABORATOR) {
                  clearAssignment();
                  return;
                }
                if (value === ADMIN_OPTION) {
                  assignToMe();
                  return;
                }
                assignCollaborator(value);
              }}
            >
              <MenuItem value={EMPTY_COLLABORATOR}>Sin asignar</MenuItem>
              {collaborators.map(collaborator => (
                <MenuItem key={collaborator.id} value={collaborator.id}>
                  {collaborator.firstName}
                </MenuItem>
              ))}
              <MenuItem value={ADMIN_OPTION}>Administrador</MenuItem>
            </CustomSelect>
          </div>
        </Tooltip>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '.5rem',
          }}
        >
          {state === 'OPEN' && (
            <TiendadaTooltip
              title="Esta conversación está abierta, los triggers no se aplicarán en esta conversación"
              placement="bottom"
            >
              <InfoOutlined />
            </TiendadaTooltip>
          )}
          <CustomButton
            color="primary"
            variant="outlined"
            disabled={
              loading ||
              (isSynchronizingConversations && conversations.length === 0) ||
              conversation?.nextConversationId
            }
            onClick={handleToggleChatState}
          >
            {loading && <CircularProgress size={20} />}
            {stateLabel} chat
          </CustomButton>
        </div>
      </div>
      <Toast open={!!error} onClose={() => {}} severity="error">
        No se pudo cambiar el estado
      </Toast>
      <Toast
        open={!!changeStateSuccess}
        onClose={() => {
          setChangeStateSuccess(false);
        }}
        severity="success"
      >
        Se cambió el estado
      </Toast>
    </div>
  );
}

StickyActions.propTypes = {};
